import { useEffect, useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { AppModal } from "global/components/AppModal";
import CustomInput from "global/components/CustomInput/CustomInput";
import filtersStyles from "../SignRequestsFilters.styles";
import {
  getViewSummary,
  SignRequestsFilterChip,
  SignRequestsFilterLookups,
  SignRequestsView,
} from "../SignRequestsFiltersHelper";
import { PencilGlyph, TrashGlyph } from "./SignRequestsIcons";

const styles = filtersStyles;

interface SaveViewDialogProps {
  open: boolean;
  /** Set when renaming; absent when saving the current filters as a new view. */
  view?: SignRequestsView | null;
  /** What a new view would capture — the applied filters plus the sort order. */
  summary: Pick<SignRequestsFilterChip, "key" | "label" | "value">[];
  /** A save is in flight: the dialog locks until it settles. */
  busy?: boolean;
  onClose: () => void;
  onSubmit: (name: string) => void;
}

/** "Save view" and "Rename view" — one dialog, the summary only on a save. */
export const SaveViewDialog = ({
  open,
  view,
  summary,
  busy = false,
  onClose,
  onSubmit,
}: SaveViewDialogProps) => {
  const [name, setName] = useState("");
  const isRename = Boolean(view);

  useEffect(() => {
    if (open) setName(view?.name ?? "");
  }, [open, view]);

  const trimmed = name.trim();

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={isRename ? "Rename view" : "Save view"}
      size="sm"
      hideIcon
      primaryLabel="Save"
      primaryType="submit"
      primaryDisabled={!trimmed}
      primaryLoading={busy}
      busy={busy}
      cancelLabel="Cancel"
      onSubmit={(event) => {
        event.preventDefault();
        if (trimmed) onSubmit(trimmed);
      }}
    >
      <CustomInput
        autoFocus
        label="Name"
        name="viewName"
        placeHolder="e.g. Urgent in flight"
        value={name}
        propsToInputElement={{ maxLength: 100 }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setName(event.target.value)
        }
      />
      {!isRename && (
        <>
          <Typography sx={styles.summaryLabel}>This view saves</Typography>
          <Box sx={styles.summaryBox}>
            {summary.map((entry) => (
              <Box key={entry.key} component="span" sx={styles.summaryChip}>
                <b>{entry.label}:</b> {entry.value}
              </Box>
            ))}
          </Box>
        </>
      )}
    </AppModal>
  );
};

interface ManageViewsDialogProps {
  open: boolean;
  views: SignRequestsView[];
  /** Resolves the ids a view stores into names for its summary line. */
  lookups: SignRequestsFilterLookups;
  onClose: () => void;
  onRename: (view: SignRequestsView) => void;
  onDelete: (view: SignRequestsView) => void;
}

/** Every view with what it filters by; rename or delete the user's own. */
export const ManageViewsDialog = ({
  open,
  views,
  lookups,
  onClose,
  onRename,
  onDelete,
}: ManageViewsDialogProps) => {
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) setConfirmingId(null);
  }, [open]);

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Manage views"
      size="md"
      hideIcon
      hideFooter
    >
      {views.map((view) => (
        <Box key={view.id} sx={styles.dialogRow}>
          <Box sx={styles.dialogRowText}>
            <Box component="span" sx={styles.dialogRowName}>
              {view.name}
            </Box>
            <Box component="span" sx={styles.dialogRowMeta}>
              {getViewSummary(view, lookups)}
            </Box>
          </Box>
          {confirmingId === view.id ? (
            <>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => {
                  setConfirmingId(null);
                  onDelete(view);
                }}
              >
                Delete
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setConfirmingId(null)}
              >
                Cancel
              </Button>
            </>
          ) : (
            !view.builtIn && (
              <>
                <IconButton
                  aria-label={`Rename ${view.name}`}
                  onClick={() => onRename(view)}
                  sx={styles.dialogAction}
                >
                  <PencilGlyph sx={styles.dialogActionGlyph} />
                </IconButton>
                <IconButton
                  aria-label={`Delete ${view.name}`}
                  onClick={() => setConfirmingId(view.id)}
                  sx={styles.dialogDangerAction}
                >
                  <TrashGlyph sx={styles.dialogActionGlyph} />
                </IconButton>
              </>
            )
          )}
        </Box>
      ))}
    </AppModal>
  );
};
